import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import { PlayCircle, ThumbsDown, ThumbsUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TypingBubble = () => (
  <div className="flex justify-start" data-testid="nexa-typing-indicator">
    <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1.5">
      {[0, 150, 300].map((d) => (
        <span key={d} className="w-1.5 h-1.5 rounded-full bg-[#0891B2] animate-bounce" style={{ animationDelay: `${d}ms` }} />
      ))}
    </div>
  </div>
);

const Feedback = ({ queryId, idx }) => {
  const [sent, setSent] = useState(null);
  const send = async (helpful) => {
    setSent(helpful);
    try { await axios.post(`${API}/help/feedback`, { query_id: queryId, helpful }); } catch { setSent(null); }
  };
  if (sent !== null) {
    return <p className="text-[11px] text-slate-500 mt-1.5" data-testid={`nexa-feedback-thanks-${idx}`}>
      {sent ? 'Thanks for the feedback!' : 'Thanks — flagged for the AudiNexa team to review.'}</p>;
  }
  return (
    <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-slate-500" data-testid={`nexa-feedback-${idx}`}>
      <span>Was this helpful?</span>
      <button onClick={() => send(true)} data-testid={`nexa-thumbs-up-${idx}`} aria-label="Helpful"
        className="p-1 rounded-md hover:bg-emerald-50 hover:text-emerald-600 transition-colors"><ThumbsUp className="w-3.5 h-3.5" /></button>
      <button onClick={() => send(false)} data-testid={`nexa-thumbs-down-${idx}`} aria-label="Not helpful"
        className="p-1 rounded-md hover:bg-rose-50 hover:text-rose-600 transition-colors"><ThumbsDown className="w-3.5 h-3.5" /></button>
    </div>
  );
};

export const NEXA_ROLES = ['Owner', 'Co-Owner', 'Admin', 'Audiologist', 'Receptionist', 'Accountant', 'Inventory Manager', 'Technician', 'Marketing'];
const ASKS_FOR_ROLE = /which role would you like to know more about/i;
const article = (r) => (/^[AEIOU]/.test(r) ? 'an' : 'a');

const RolePicker = ({ idx, onPick }) => (
  <div className="mt-2 flex flex-wrap gap-1.5 max-w-[90%]" data-testid={`nexa-role-picker-${idx}`}>
    {NEXA_ROLES.map((r) => (
      <button key={r} type="button" onClick={() => onPick(`What can ${article(r)} ${r} do and not do?`)}
        data-testid={`nexa-role-chip-${r.toLowerCase().replace(/\s+/g, '-')}`}
        className="text-[12px] font-medium text-[#0E7490] bg-white border border-[#0891B2]/40 hover:bg-[#ECFEFF] rounded-full px-3 py-1 transition-colors">
        {r}
      </button>
    ))}
  </div>
);

export const NexaMessage = ({ msg, idx, onPickRole }) => {
  if (msg.from === 'user') {
    return (
      <div className="flex justify-end" data-testid={`nexa-msg-user-${idx}`}>
        <div className="max-w-[85%] bg-[#0F1D3A] text-white text-[13px] leading-relaxed rounded-2xl rounded-br-sm px-3.5 py-2.5 whitespace-pre-wrap">{msg.text}</div>
      </div>
    );
  }
  return (
    <div className="flex flex-col items-start" data-testid={`nexa-msg-assistant-${idx}`} data-status={msg.status || ''} data-cached={msg.cached ? 'true' : 'false'}>
      <div className="max-w-[90%] bg-white border border-slate-200 text-slate-800 text-[13px] leading-relaxed rounded-2xl rounded-bl-sm px-3.5 py-2.5 nexa-md">
        <ReactMarkdown>{msg.text}</ReactMarkdown>
      </div>
      {msg.video && (
        <a href={msg.video.url} target="_blank" rel="noopener noreferrer" data-testid={`nexa-watch-video-${idx}`}
          className="mt-2 inline-flex items-center gap-1.5 text-[12px] font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-full px-3 py-1.5 transition-colors">
          <PlayCircle className="w-4 h-4" /> Watch Video
        </a>
      )}
      {onPickRole && ASKS_FOR_ROLE.test(msg.text || '') && <RolePicker idx={idx} onPick={onPickRole} />}
      {msg.queryId && <Feedback queryId={msg.queryId} idx={idx} />}
    </div>
  );
};
